import React, { Component } from "react";
import { AxiosResponse } from "axios";
import ReactHtmlParser from "react-html-parser";
import { getRequest, putRequest } from "../../utils/httpRequest";
import { errorNotification } from "../../utils/helpers";
import {
  GROUP_LABELS,
  GROUP_ORDER,
  NotificationResource,
  notificationGroup,
  openNotification,
} from "../../utils/notifications";
import { CurrentUserResource } from "../../stores/CurrentUserStore/Types";
import AlertIcon from "./AlertIcon";
import Pagination from "./Pagination";

interface XpAlert {
  id: number;
  html: string;
  xp: number;
  created_at: string;
  opened_xp: boolean;
  created_at_timestamp: string;
}

interface Paged<T> {
  page: T[];
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface XpPayload extends Paged<XpAlert> {
  xp_this_week: number;
}

interface NotificationsPayload extends Paged<NotificationResource> {
  unread_count: number;
}

interface Props {
  currentUser: CurrentUserResource;
  currentUserLoaded: boolean;
  getCurrentUser: () => void;
}

type Tab = "notifications" | "xp";

interface State {
  tab: Tab;
  filter: string;
  notifications: NotificationResource[];
  page: number;
  totalPages: number;
  totalCount: number;
  unreadCount: number;
  everHadAny: boolean;
  xpAlerts: XpAlert[];
  xpPage: number;
  xpTotalPages: number;
  xpTotalCount: number;
  xpThisWeek: number;
  xpLoaded: boolean;
  loading: boolean;
  toast: string;
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "reply", label: "Replies" },
  { key: "mark", label: "Marking" },
  { key: "cert-badge", label: "Certificates & badges" },
  { key: "course-news", label: "Courses & updates" },
];

const TOAST_MS = 3200;

class Notifications extends Component<Props, State> {
  private toastTimer?: number;

  constructor(props: Props) {
    super(props);

    this.state = {
      tab: "notifications",
      filter: "all",
      notifications: [],
      page: 1,
      totalPages: 0,
      totalCount: 0,
      unreadCount: 0,
      everHadAny: false,
      xpAlerts: [],
      xpPage: 1,
      xpTotalPages: 0,
      xpTotalCount: 0,
      xpThisWeek: 0,
      xpLoaded: false,
      loading: true,
      toast: "",
    };
  }

  componentDidMount() {
    document.title = "Your alerts | 42courses";
    this.fetchNotifications();
  }

  componentWillUnmount() {
    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
    }
  }

  fetchNotifications = (page = this.state.page, filter = this.state.filter) => {
    this.setState({ loading: true });

    return getRequest(
      `/api/notifications/paged.json?page=${page}&filter=${filter}`,
    )
      .then((response: AxiosResponse<NotificationsPayload>) => {
        const { data } = response;

        this.setState((previous) => ({
          notifications: data.page,
          page: data.current_page,
          totalPages: data.total_pages,
          totalCount: data.total_count,
          unreadCount: data.unread_count,
          // "Nothing here yet" is about having no alerts at all, not about a
          // filter coming up empty, so only an unfiltered result can set it.
          everHadAny:
            filter === "all" ? data.total_count > 0 : previous.everHadAny,
          loading: false,
        }));
      })
      .catch((error) => {
        this.setState({ loading: false });
        errorNotification(
          "Error loading your alerts",
          JSON.stringify(error.response?.data || error.message),
        );
      });
  };

  fetchXpAlerts = (page = this.state.xpPage) => {
    this.setState({ loading: true });

    return getRequest(`/api/achievements/xp/recent.json?page=${page}`)
      .then((response: AxiosResponse<XpPayload>) => {
        const { data } = response;

        this.setState({
          xpAlerts: data.page || [],
          xpPage: data.current_page || page,
          xpTotalPages: data.total_pages || 0,
          xpTotalCount: data.total_count || 0,
          xpThisWeek: data.xp_this_week || 0,
          xpLoaded: true,
          loading: false,
        });
      })
      .catch((error) => {
        this.setState({ loading: false, xpLoaded: true });
        errorNotification(
          "Error loading your XP",
          JSON.stringify(error.response?.data || error.message),
        );
      });
  };

  setTab = (tab: Tab) => {
    this.setState({ tab });

    if (tab === "xp" && !this.state.xpLoaded) {
      this.fetchXpAlerts(1);
    }
  };

  setFilter = (filter: string) => {
    // Back to page one: page 4 of "All" is rarely page 4 of "Unread".
    this.setState({ filter, page: 1 });
    this.fetchNotifications(1, filter);
  };

  changePage = (page: number) => {
    this.setState({ page });
    this.fetchNotifications(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  changeXpPage = (page: number) => {
    this.setState({ xpPage: page });
    this.fetchXpAlerts(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  showToast = (message: string) => {
    this.setState({ toast: message });
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(
      () => this.setState({ toast: "" }),
      TOAST_MS,
    );
  };

  markAllAsRead = () => {
    this.setState({ loading: true });

    return putRequest("/api/notifications/update_all.json", {})
      .then(() => {
        this.props.getCurrentUser();
        this.showToast("All caught up. Not a single ping left waiting.");

        // Refetch rather than patch state locally: on the Unread filter the
        // list should now be empty, and the page count changes with it.
        return this.fetchNotifications();
      })
      .catch(() => {
        this.setState({ loading: false });
        errorNotification("Could not mark your alerts as read");
      });
  };

  openAlert = (
    event: React.MouseEvent<HTMLAnchorElement>,
    notification: NotificationResource,
  ) => {
    if (!notification.href) {
      return;
    }

    event.preventDefault();
    openNotification(notification, this.props.getCurrentUser);
  };

  renderAlert = (notification: NotificationResource) => (
    <a
      className={`alert-item ${notification.opened ? "" : "is-unread"} ${
        notification.href ? "" : "no-href"
      }`}
      href={notification.href || undefined}
      onClick={(event) => this.openAlert(event, notification)}
      key={notification.id}
    >
      <AlertIcon notification={notification} baseClass="alert-ic" />
      <span className="alert-body">
        <span className="alert-text">
          {/* @ts-ignore */}
          <strong>{ReactHtmlParser(notification.title)}</strong>
          {/* @ts-ignore */}
          {notification.body && <> {ReactHtmlParser(notification.body)}</>}
        </span>
        {notification.target_name && (
          <span className="alert-target">
            {/* @ts-ignore */}
            {ReactHtmlParser(notification.target_name)}
          </span>
        )}
        <span className="alert-time">
          {/* @ts-ignore */}
          {ReactHtmlParser(notification.created_at)}
        </span>
      </span>
    </a>
  );

  renderGroups = () => {
    const { notifications } = this.state;

    return (
      <div className="notif-groups">
        {GROUP_ORDER.map((group) => {
          const inGroup = notifications.filter(
            (notification) =>
              notificationGroup(notification.created_at_timestamp) === group,
          );

          if (inGroup.length === 0) {
            return null;
          }

          return (
            <div className="notif-group" key={group}>
              <h2 className="group-heading">{GROUP_LABELS[group]}</h2>
              {inGroup.map(this.renderAlert)}
            </div>
          );
        })}
      </div>
    );
  };

  renderEmpty = () => {
    const { everHadAny, filter } = this.state;

    if (everHadAny && filter !== "all") {
      const active = FILTERS.find((option) => option.key === filter);

      return (
        <div className="empty-state">
          <span className="es-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <use href="#icon-bell" />
            </svg>
          </span>
          <h2>Nothing under {active ? active.label.toLowerCase() : "this filter"}</h2>
          <p>
            No alerts match that filter right now. Try another one, or switch
            back to All to see everything.
          </p>
        </div>
      );
    }

    return (
      <div className="empty-state">
        <span className="es-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <use href="#icon-bell" />
          </svg>
        </span>
        <h2>Nothing here yet</h2>
        <p>
          The moment someone replies, a tutor marks your work, or a badge lands,
          it will turn up right here. Until then, this space is deliberately
          quiet.
        </p>
      </div>
    );
  };

  renderNotificationsPanel = () => {
    const { notifications, loading, page, totalPages } = this.state;

    return (
      <div
        className="notif-panel-page"
        id="panel-notifications"
        role="tabpanel"
        aria-labelledby="tab-notifications"
        hidden={this.state.tab !== "notifications"}
      >
        {notifications.length > 0 ? this.renderGroups() : !loading && this.renderEmpty()}

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          label="Notifications pages"
          onChange={this.changePage}
        />
      </div>
    );
  };

  renderXpPanel = () => {
    const {
      xpAlerts,
      xpPage,
      xpTotalPages,
      xpTotalCount,
      xpThisWeek,
      loading,
      xpLoaded,
    } = this.state;
    const { currentUser } = this.props;
    const totalXp = currentUser.kind === "current-user" ? currentUser.xp : 0;

    return (
      <div
        className="notif-panel-page"
        id="panel-xp"
        role="tabpanel"
        aria-labelledby="tab-xp"
        hidden={this.state.tab !== "xp"}
      >
        <div className="xp-stat-strip">
          <div className="stat">
            <div className="stat-num">{totalXp.toLocaleString()}</div>
            <div className="stat-label">Total XP</div>
          </div>
          <div className="stat">
            <div className="stat-num">{xpThisWeek.toLocaleString()}</div>
            <div className="stat-label">XP this week</div>
          </div>
          <div className="stat">
            <div className="stat-num">{xpTotalCount.toLocaleString()}</div>
            <div className="stat-label">Wins logged</div>
          </div>
        </div>

        {xpAlerts.length > 0 ? (
          <div className="xp-list">
            {xpAlerts.map((alert) => (
              <div className="xp-item" key={alert.id}>
                <span className="xp-pts">+{alert.xp}</span>
                <span className="xp-desc">
                  {/* @ts-ignore */}
                  {ReactHtmlParser(alert.html)}
                </span>
                <span className="xp-time">
                  {/* @ts-ignore */}
                  {ReactHtmlParser(alert.created_at)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          !loading &&
          xpLoaded && (
            <div className="empty-state">
              <span className="es-icon" aria-hidden="true">
                <svg viewBox="0 0 32 32">
                  <use href="#icon-bulb" />
                </svg>
              </span>
              <h2>No XP yet</h2>
              <p>Your wins will show up here as you work through a course.</p>
            </div>
          )
        )}

        <Pagination
          currentPage={xpPage}
          totalPages={xpTotalPages}
          label="XP and wins pages"
          onChange={this.changeXpPage}
        />
      </div>
    );
  };

  render() {
    const { tab, filter, unreadCount, loading, toast } = this.state;
    const { currentUser, currentUserLoaded } = this.props;

    if (currentUserLoaded && currentUser.kind !== "current-user") {
      return (
        <main className="alerts-page">
          <section className="alerts-head">
            <div className="container">
              <h1>Your alerts</h1>
              <p className="alerts-sub">
                Sign in to see every reply, mark and certificate 42courses has
                sent you.
              </p>
            </div>
          </section>
        </main>
      );
    }

    return (
      <main className="alerts-page">
        <section className="alerts-head" aria-labelledby="alerts-h1">
          <div className="container">
            <div className="alerts-head-top">
              <div>
                <h1 id="alerts-h1">Your alerts</h1>
                <p className="alerts-sub">
                  Every ping, mark and certificate lands here, with enough room
                  to actually read it this time.
                </p>
              </div>
              <div className="alerts-head-actions">
                <span className="unread-badge">{unreadCount} unread</span>
                <button
                  className="btn btn-outline btn--compact"
                  type="button"
                  onClick={this.markAllAsRead}
                  disabled={unreadCount === 0 || loading}
                >
                  Mark all read
                </button>
              </div>
            </div>

            {/* Hidden on the XP tab: the pills filter notifications only, and
                leaving dead controls on screen just invites a confused click. */}
            <div
              className="filter-pills"
              role="group"
              aria-label="Filter alerts by type"
              hidden={tab !== "notifications"}
            >
              {FILTERS.map((option) => (
                <button
                  className="filter-pill"
                  type="button"
                  key={option.key}
                  aria-pressed={filter === option.key}
                  onClick={() => this.setFilter(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="alerts-body">
          <div className="container">
            <div
              className="alerts-tabs notif-tabs"
              role="tablist"
              aria-label="Alert type"
            >
              <button
                className="notif-tab"
                type="button"
                role="tab"
                id="tab-notifications"
                aria-selected={tab === "notifications"}
                aria-controls="panel-notifications"
                tabIndex={tab === "notifications" ? undefined : -1}
                onClick={() => this.setTab("notifications")}
              >
                Notifications
              </button>
              <button
                className="notif-tab"
                type="button"
                role="tab"
                id="tab-xp"
                aria-selected={tab === "xp"}
                aria-controls="panel-xp"
                tabIndex={tab === "xp" ? undefined : -1}
                onClick={() => this.setTab("xp")}
              >
                XP &amp; Wins
              </button>
            </div>

            {loading && (
              <div className="alerts-loading">
                <div className="ui active centered inline loader" />
              </div>
            )}

            {this.renderNotificationsPanel()}
            {this.renderXpPanel()}
          </div>
        </section>

        <div
          className={`toast ${toast ? "is-visible" : ""}`}
          role="status"
          aria-live="polite"
        >
          {toast}
        </div>
      </main>
    );
  }
}

export default Notifications;
